import { useEffect, useState } from "react";
import { api, ROLE_LABEL } from "@/lib/api";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";
import { Check, X, ShieldAlert } from "lucide-react";

const ROLES = ["band_member", "staff_member", "councillor", "head_councillor", "deputy_chief", "chief", "executive_director", "band_registry_clerk"];

export default function ClerkConsole() {
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [all, setAll] = useState([]);

  const load = () => {
    api.get("/registry/pending").then(r=>setPending(r.data));
    api.get("/registry/all").then(r=>setAll(r.data));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="label-mono text-[#ef5b6b] flex items-center gap-2"><ShieldAlert size={14}/> BAND REGISTRY CONSOLE</div>
      <h1 className="font-display text-4xl mt-2">Registry verification</h1>
      <p className="text-white/60 mt-2 max-w-2xl">Review submitted applications against legacy documents. Approve to grant portal access and assign role.</p>

      <div className="flex gap-1 mt-8 border-b border-white/10">
        {[["pending",`Pending (${pending.length})`], ["all",`All members (${all.length})`]].map(([k,v]) => (
          <button key={k} onClick={()=>setTab(k)} className={`px-4 py-2 font-mono text-[11px] uppercase tracking-widest border-b-2 ${tab===k?"border-[#ef5b6b] text-[#ef5b6b]":"border-transparent text-white/50"}`} data-testid={`clerk-tab-${k}`}>{v}</button>
        ))}
      </div>

      <div className="mt-8 space-y-3">
        {(tab === "pending" ? pending : all).length === 0 && <div className="font-mono text-xs text-white/40">Nothing to review.</div>}
        {(tab === "pending" ? pending : all).map(u => (
          <RegistryRow key={u.id} u={u} onDone={load} isPending={tab === "pending"} />
        ))}
      </div>
    </div>
  );
}

function RegistryRow({ u, onDone, isPending }) {
  const [role, setRole] = useState(u.role || "band_member");
  const [notes, setNotes] = useState("");
  const [expanded, setExpanded] = useState(isPending);
  const confirm = useConfirm();

  const approve = async () => {
    try {
      await api.post(`/registry/${u.id}/approve`, { role, notes });
      toast.success(`${u.first_name} ${u.last_name} approved as ${role.replace(/_/g," ")}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Approval failed"); }
  };
  const reject = async () => {
    const ok = await confirm({ title: "Reject application?", message: `${u.first_name} ${u.last_name} · ${u.email}`, destructive: true, confirmLabel: "Reject" });
    if (!ok) return;
    try {
      await api.post(`/registry/${u.id}/reject`, { role, notes });
      toast.success("Application rejected");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Rejection failed"); }
  };

  const statusColor = u.status === "approved" ? "#89b4fa" : u.status === "rejected" ? "#ef5b6b" : "#d4b876";

  return (
    <div className="panel p-5" data-testid={`registry-row-${u.id}`}>
      <div className="flex justify-between items-start gap-3">
        <button onClick={()=>setExpanded(!expanded)} className="text-left flex-1">
          <div className="flex items-baseline flex-wrap gap-3">
            <div className="font-display text-xl">{u.first_name} {u.last_name}</div>
            <span className="tag" style={{color: statusColor}}>{u.status}</span>
            <span className="font-mono text-[10px] text-white/50 uppercase tracking-widest">{ROLE_LABEL[u.role]}</span>
          </div>
          <div className="font-mono text-xs text-white/50 mt-1">{u.email} · Registry #{u.band_registry_number}</div>
        </button>
      </div>

      {expanded && (
        <div className="mt-4 grid md:grid-cols-2 gap-4 text-sm font-mono">
          <div><span className="label-mono block">DOB</span>{u.date_of_birth}</div>
          <div><span className="label-mono block">Treaty Status</span>{u.treaty_status_number || "—"}</div>
          <div><span className="label-mono block">Parents</span>{u.parents_names || "—"}</div>
          <div><span className="label-mono block">Community</span>{u.community_of_residence || "—"}</div>
          <div><span className="label-mono block">Phone</span>{u.phone || "—"}</div>
          <div><span className="label-mono block">Submitted</span>{u.created_at?.substring(0,19).replace('T',' ')}</div>

          {isPending && (
            <div className="md:col-span-2 mt-2 border-t border-white/10 pt-4 space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="label-mono block mb-1">Assign role on approval</label>
                  <select value={role} onChange={e=>setRole(e.target.value)} className="w-full px-3 py-2 font-mono text-sm" data-testid={`role-select-${u.id}`}>
                    {ROLES.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-mono block mb-1">Clerk notes</label>
                  <input value={notes} onChange={e=>setNotes(e.target.value)} className="w-full px-3 py-2 font-mono text-sm" data-testid={`notes-input-${u.id}`} />
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={approve} className="btn-cta primary" data-testid={`approve-${u.id}`}><Check size={12}/> Approve & assign</button>
                <button onClick={reject} className="btn-cta danger" data-testid={`reject-${u.id}`}><X size={12}/> Reject</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
